import { CloudIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { OverflowText } from '@/components/OverflowText';
import { getColumnWidth } from '@/components/table/columns/registry';
import { Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Agent } from '@/hooks/useAgents';
import { AgentStatusBadge } from '@/sections/agents/components';
import { AgentIPAddressesDropdown } from '@/sections/agents/components/cells/AgentIPAddressesDropdown';
import { getOsIcon } from '@/sections/agents/utils/osIcons';
import { formatDate } from '@/utils/date.util';

/**
 * Sortable columns configuration for agents table
 */
export const sortableColumnsConfig = {
  fixed: [],
  active: [
    {
      label: 'Status',
      id: 'status' as keyof Agent,
      disabled: false,
      isDefault: false,
    },
    { label: 'Agent', id: 'client_id', disabled: false, isDefault: false },
    { label: 'Asset', id: 'fqdn', disabled: true, isDefault: true },
    {
      label: 'Last Seen',
      id: 'last_seen_at',
      disabled: false,
      isDefault: false,
    },
  ],
  available: [],
};

/**
 * Column definitions for the agents table
 *
 * This configuration defines the structure and rendering logic
 * for each column in the agents table.
 *
 * Column widths are managed centrally in:
 * @see modules/chariot/ui/src/components/table/columns/registry.ts
 *
 * Migration: 2025-10-23
 * - Migrated from local hardcoded widths to centralized registry
 * - Maintains all agent-specific rendering logic and data sources
 * - Cosmetic properties (width, label, sortable) now centrally managed
 */
export const defaultColumns: Columns<Agent> = [
  {
    label: 'Status',
    id: 'status' as keyof Agent,
    ...getColumnWidth('agent', 'status'),
    align: 'center',
    sorting: true,
    cell: (data: Agent) => {
      return <AgentStatusBadge agent={data} />;
    },
  },
  {
    label: 'Agent',
    id: 'client_id',
    ...getColumnWidth('agent', 'client_id'),
    sorting: true,
    cell: (data: Agent) => {
      // Check if Cloudflare tunnel is configured
      const isCloudflareConfigured =
        data.health_check?.cloudflared_status?.status === 'running' &&
        data.health_check?.cloudflared_status?.connected === 'true';

      return (
        <div className="flex items-center gap-2">
          <span>{data.client_id}</span>
          {isCloudflareConfigured && (
            <Tooltip title="Cloudflare Tunnel Configured" placement="top">
              <CloudIcon className="text-chariot-stroke-info size-4 shrink-0" />
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    label: 'Asset',
    id: 'fqdn',
    ...getColumnWidth('agent', 'fqdn'),
    sorting: true,
    cell: (data: Agent) => {
      const fqdn = data.fqdn || data.hostname;
      const ips = [
        ...new Set(
          data.network_interfaces?.flatMap(
            networkInterface => networkInterface.ip_addresses
          ) ?? []
        ),
      ].sort((a, b) => a.localeCompare(b));

      if (!fqdn) return '-';

      return (
        <div className="flex w-full flex-col gap-1 overflow-hidden py-3">
          <div className="flex items-center gap-2">
            {getOsIcon(data.os)}
            <CopyToClipboard>
              <OverflowText
                className="group-[.is-selected]:text-chariot-text-primary shrink cursor-pointer truncate font-bold"
                text={fqdn}
              />
            </CopyToClipboard>
          </div>
          <AgentIPAddressesDropdown ips={ips} />
        </div>
      );
    },
  },
  {
    label: 'Last Seen',
    id: 'last_seen_at',
    ...getColumnWidth('agent', 'last_seen_at'),
    sorting: true,
    cell: (data: Agent) => {
      if (!data.last_seen_at) return <div className="pl-10">-</div>;

      const isoString = new Date(
        Math.floor(Number(data.last_seen_at) / 1000)
      ).toISOString();

      return (
        <Tooltip title={isoString} placement="bottom-end">
          {formatDate(isoString)}
        </Tooltip>
      );
    },
  },
];
